import moment from 'moment';
import { isNavigationFailure, NavigationFailureType, RouteLocationNamedRaw, RouteLocationRaw, Router } from 'vue-router';
import Booking from '@/booking/models/Booking';
import Dossier from '@/dossier/models/Dossier';
import DossierWorkshopPlanning from '@/dossier/models/DossierWorkshopPlanning';
import PlannerParams from '@/planner/utils/PlannerParams';
import { OutletId } from '@/shared/models/Outlet';
import { isPresent, present } from '@/shared/utils/typescript';
import SearchResultDossier from '../models/SearchResultDossier';
import SearchResultPlanning from '../models/SearchResultPlanning';
import { BOOKING_OVERVIEW_ROUTE } from './constants';
import { DossierId } from '@/dossier/models/AbstractDossier';

type GotoMethod = 'push' | 'replace';

function guessGotoMethod(newLocation: RouteLocationRaw, router: Router): GotoMethod {
  return typeof newLocation === 'string' || !('name' in newLocation) || newLocation.name !== router.currentRoute.value.name ? 'push' : 'replace';
}

/**
 * Convenience function that performs a router push/replace and deals with the "fake errors" it often produces
 * E.g.: "Uncaught (in promise) Error: Redirected when..." etc
 * Such errors are in fact normal behavior when caused by router guards purposefully cancelling/redirecting a navigation due to certain business logic,
 * but are thrown to stop any subsequent code that might rely on the success of the original navigation.
 * IMHO the latter is a rare problem that should've been solved with a return value rather than an exception
 * (i.e. when you expect something to happen it's not an exception).
 */
export async function goto(newLocation: RouteLocationRaw, router: Router, method = guessGotoMethod(newLocation, router)): Promise<void> {
  try {
    await router[method](newLocation);
  } catch (error) {
    if (isNavigationFailure(error)) {
      if (import.meta.env.DEV) {
        const reason = NavigationFailureType[error.type] || '?';
        // eslint-disable-next-line no-console
        console.log('navigation error: %s - %s', reason, error.message);
      }
    } else {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }
}

export function gotoBookingOverview(router: Router, method?: GotoMethod): Promise<void> {
  return goto({ name: BOOKING_OVERVIEW_ROUTE }, router, method);
}

export function gotoWorkshopPlanningForBooking(booking: Booking, router: Router): Promise<void> {
  const params = PlannerParams.basic(present(booking.appointmentOn), present(booking.outlet?.id));
  const location = isPresent(booking.dossier?.id) ? params.asDossierLocation(booking.dossier?.id) : params.asRootLocation();
  return goto(location, router);
}

export function getWorkshopPlanningLink(planning: DossierWorkshopPlanning, dossier: Dossier): RouteLocationNamedRaw {
  // Note: A planning can be assigned to a mechanic that's member of a different team than the team assigned to the dossier.
  return PlannerParams.basic(
    planning.start,
    dossier.outletId,
    dossier.workshopTeamId,
  ).asEventLocation(planning.eventId, false, dossier.id);
}

export function gotoWorkshopPlanningForSearchResult(planning: SearchResultPlanning, outletId: OutletId, router: Router, dossierId?: DossierId): Promise<void> {
  return goto(PlannerParams.basic(present(planning.scheduledFrom), outletId).asEventLocation(planning.id, false, dossierId), router);
}

export function gotoWorkshopUnplannedEvent(dossier: Dossier | SearchResultDossier, router: Router): Promise<void> {
  return goto(getWorkshopUnplannedEventLocation(dossier), router);
}

export function getWorkshopUnplannedEventLocation(dossier: Dossier | SearchResultDossier): RouteLocationNamedRaw {
  const selectedDate = dossier instanceof Dossier ? dossier.appointmentOn : dossier.scheduledFrom;
  const outletId = dossier instanceof Dossier ? dossier.outletId : dossier.outletId;
  const teamId = dossier instanceof Dossier ? dossier.workshopTeamId : dossier.workshopTeamId;

  const now = moment();
  const targetDate = selectedDate?.isSameOrAfter(now, 'day') ? selectedDate : now;

  return PlannerParams
    .basic(targetDate, present(outletId))
    .withTeam(teamId ?? null)
    .asDossierLocation(dossier.id);
}